/**
 * Modal for uploading and managing team member profile pictures
 */
import { useState } from 'react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog'
import { Upload, X, User, CheckCircle } from 'lucide-react'

// Import Dialog components from shadcn
const Dialog = ({ children, open, onOpenChange }: any) => (
  <div className={open ? 'block' : 'hidden'}>{children}</div>
)

const DialogTrigger = ({ children, asChild }: any) => children

const DialogContent = ({ children, className }: any) => (
  <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50`}>
    <div className={`bg-white rounded-lg p-6 max-w-md w-full mx-4 ${className}`}>
      {children}
    </div>
  </div>
)

const DialogHeader = ({ children }: any) => (
  <div className="mb-4">{children}</div>
)

const DialogTitle = ({ children }: any) => (
  <h2 className="text-lg font-semibold">{children}</h2>
)

interface TeamMember {
  name: string
  position: string
  image: string
  linkedin?: string
  email?: string
}

interface ImageUploadModalProps {
  teamMember: TeamMember
  onImageUpdate: (name: string, imageUrl: string) => void
}

export default function ImageUploadModal({ teamMember, onImageUpdate }: ImageUploadModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [imageUrl, setImageUrl] = useState(teamMember.image)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      // Validate URL
      if (!imageUrl.trim()) {
        alert('Please enter an image URL')
        return
      }

      // Test image load
      await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = resolve
        img.onerror = reject
        img.src = imageUrl
      })

      onImageUpdate(teamMember.name, imageUrl)
      setIsOpen(false)
    } catch (error) {
      alert('Invalid image URL. Please check the link and try again.')
      console.error('Image validation error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Create object URL for preview
    const objectUrl = URL.createObjectURL(file)
    setImageUrl(objectUrl)
    
    // Note: In a real implementation, you'd upload to a server
    // For now, we'll use the object URL (valid until page unload)
    console.log('File selected:', file.name)
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="bg-transparent">
          <Upload className="h-4 w-4 mr-2" />
          Change Photo
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update {teamMember.name}'s Profile Picture</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Image Preview */}
          <div className="flex justify-center">
            <div className="relative w-32 h-32 rounded-full overflow-hidden border-2 border-gray-200">
              <img
                src={imageUrl}
                alt={teamMember.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/749302b2-9eda-45d3-8dfe-4d6d47838337.jpg'
                }}
              />
            </div>
          </div>

          {/* URL Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Image URL
            </label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="https://example.com/image.jpg"
            />
            <p className="text-xs text-gray-500">
              Enter a direct image URL or upload a file
            </p>
          </div>

          {/* File Upload Alternative */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Upload File
            </label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <Button variant="outline" className="w-full bg-transparent" type="button">
                <Upload className="h-4 w-4 mr-2" />
                Choose File
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              JPG, PNG, or WebP (max 5MB)
            </p>
          </div>

          {/* Image Requirements */}
          <div className="p-3 bg-orange-50 rounded-lg">
            <h4 className="text-sm font-semibold text-orange-800 mb-2">Image Requirements</h4>
            <ul className="text-xs text-orange-700 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-3 w-3" />
                Square or portrait orientation
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-3 w-3" />
                High resolution (min 400x400px)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-3 w-3" />
                Professional headshot preferred
              </li>
            </ul>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              className="bg-transparent"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              <User className="h-4 w-4 mr-2" />
              {isLoading ? 'Updating...' : 'Update Photo'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
